import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  inject,
  OnDestroy,
  ElementRef,
  Component,
  EnvironmentInjector,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal, ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-default-tooltip',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="max-h-[400px] max-w-[500px] overflow-auto p-3 bg-gray-800 bg-opacity-95 text-white rounded text-xs shadow-lg font-mono"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()">
      <pre class="m-0 whitespace-pre-wrap break-words">{{ data | json }}</pre>
    </div>
  `,
})
export class DefaultTooltipComponent {
  data: any;

  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

@Directive({
  selector: '[appTooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'showTooltip()',
    '(mouseleave)': 'hideTooltip()',
  },
})
export class TooltipOverlayDirective implements OnDestroy {
  @Input('appTooltip') tooltipData: any;
  @Input() tooltipTemplate?: TemplateRef<any>;
  @Input() tooltipMinLength = 50; // Minimum data length to show tooltip
  @Input() tooltipDelay = 100; // Hide delay in ms

  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private elementRef = inject(ElementRef);
  private environmentInjector = inject(EnvironmentInjector);

  private overlayRef: OverlayRef | null = null;
  private hideTimeoutId: any;
  private isTooltipHovered = false;

  // Static properties to manage global tooltip state
  private static activeTooltip: TooltipOverlayDirective | null = null;
  private static globalHideTimeout: any = null;

  ngOnDestroy(): void {
    this.cleanup();
  }

  showTooltip(): void {
    if (!this.shouldShowTooltip()) {
      return;
    }

    // Clear any global hide timeout and hide previous tooltip immediately
    this.clearGlobalHideTimeout();
    this.hideActiveTooltip();

    this.clearHideTimeout();
    this.disposeOverlay();

    // Set this as the active tooltip
    TooltipOverlayDirective.activeTooltip = this;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: 8,
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -8,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    if (this.tooltipTemplate) {
      // Use provided template
      const portal = new TemplatePortal(this.tooltipTemplate, this.viewContainerRef, {
        $implicit: this.tooltipData,
      });
      this.overlayRef.attach(portal);
    } else {
      // Use default component template
      const componentPortal = new ComponentPortal(DefaultTooltipComponent, null, this.environmentInjector);
      const componentRef = this.overlayRef.attach(componentPortal);

      // Set component inputs and event handlers
      componentRef.instance.data = this.tooltipData;
      componentRef.instance.onMouseEnter = () => this.onTooltipMouseEnter();
      componentRef.instance.onMouseLeave = () => this.onTooltipMouseLeave();

      // Trigger change detection
      componentRef.changeDetectorRef.detectChanges();
    }
  }

  hideTooltip(): void {
    // Use global timeout to allow other tooltips to override
    TooltipOverlayDirective.globalHideTimeout = setTimeout(() => {
      if (TooltipOverlayDirective.activeTooltip === this && !this.isTooltipHovered) {
        this.disposeOverlay();
        TooltipOverlayDirective.activeTooltip = null;
      }
    }, this.tooltipDelay);
  }

  onTooltipMouseEnter(): void {
    this.isTooltipHovered = true;
    this.clearGlobalHideTimeout();
  }

  onTooltipMouseLeave(): void {
    this.isTooltipHovered = false;
    this.disposeOverlay();
    if (TooltipOverlayDirective.activeTooltip === this) {
      TooltipOverlayDirective.activeTooltip = null;
    }
  }

  private hideActiveTooltip(): void {
    if (TooltipOverlayDirective.activeTooltip && TooltipOverlayDirective.activeTooltip !== this) {
      TooltipOverlayDirective.activeTooltip.disposeOverlay();
      TooltipOverlayDirective.activeTooltip = null;
    }
  }

  private clearGlobalHideTimeout(): void {
    if (TooltipOverlayDirective.globalHideTimeout) {
      clearTimeout(TooltipOverlayDirective.globalHideTimeout);
      TooltipOverlayDirective.globalHideTimeout = null;
    }
  }

  private shouldShowTooltip(): boolean {
    if (!this.tooltipData) return false;

    if (typeof this.tooltipData === 'string') {
      return this.tooltipData.length >= this.tooltipMinLength;
    }

    return true; // Show for objects/arrays
  }

  private clearHideTimeout(): void {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  private disposeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private cleanup(): void {
    this.clearHideTimeout();
    this.clearGlobalHideTimeout();
    this.disposeOverlay();
    if (TooltipOverlayDirective.activeTooltip === this) {
      TooltipOverlayDirective.activeTooltip = null;
    }
  }
}
